/**
 * Menu in Page Header.
 */
export class Menu {

	#menuTrigger;

	#navTriggers;

	#scrollPosition;

	#headerEl;

	#langSwitcherTrigger;

	#navTopTriggers;

	#lastScrollTop = 0;

	/**
	 * Constructor
	 */
	constructor (){

		this.#menuTrigger = document.querySelector( '.js-page-header-menu-trigger' );
		this.#headerEl = document.querySelector( '.page-header' );

		if ( !this.#menuTrigger || !this.#headerEl ) {

			return;

		}

		this.#navTriggers = this.#headerEl.querySelectorAll( '.js-menu-header-trigger' );
		this.#langSwitcherTrigger = this.#headerEl.querySelector( '.page-header__lang .c-lang-switcher__trigger' );
		this.#navTopTriggers = this.#headerEl.querySelectorAll( '.page-header__nav-top .js-nav-top-trigger' );

		this.#init();

	}

	/**
	 * Initialize menu
	 *
	 * @return {void}
	 */
	#init (){

		this.#menuTrigger.addEventListener( 'click', () => this.#handleMenuTrigger() );
		this.#navTriggers.forEach( ( trigger ) => {

			trigger.addEventListener( 'click', () => this.#handleNavTrigger( trigger ) );

		} );

		if ( this.#langSwitcherTrigger ) {

			this.#langSwitcherTrigger.addEventListener( 'click', () => this.#handleLangSwitcherTrigger() );

			const langSwitcherEl = this.#langSwitcherTrigger.closest( '.c-lang-switcher' );

			if ( langSwitcherEl ) {

				langSwitcherEl.addEventListener( 'mouseleave', () => this.#closeLangSwitcher() );

			}

		}

		this.#navTopTriggers.forEach( ( trigger ) => {

			trigger.addEventListener( 'click', () => this.#handleNavTopTrigger( trigger ) );

		} );

		this.#headerEl.querySelectorAll( '.c-nav-top__item--has-children' ).forEach( ( item ) => {

			item.addEventListener( 'mouseleave', () => this.#closeNavTopItem( item ) );

		} );

		this.#headerEl.addEventListener( 'mouseleave', () => this.#closeAllNavSubmenus() );
		document.body.addEventListener( 'click', this.#handleBodyClick.bind( this ) );
		document.addEventListener( 'keydown', this.#handleKeyDown.bind( this ) );

		this.#setBodyPadding();
		window.addEventListener( 'resize', this.#setBodyPadding.bind( this ) );
		window.addEventListener( 'scroll', this.#handleScrollPage.bind( this ) );

	}

	/**
	 * Handles the scroll event on the page and toggles the 'page-header--hidden' class on the header element based on scroll direction.
	 *
	 * @return {void}
	 */
	#handleScrollPage (){

		const scrollTop = window.pageYOffset || document.documentElement.scrollTop;

		if ( scrollTop > this.#lastScrollTop && scrollTop > this.#headerEl.scrollHeight ) {

			this.#headerEl.classList.add( 'page-header--hidden' );

		} else {

			this.#headerEl.classList.remove( 'page-header--hidden' );

		}
		this.#lastScrollTop = scrollTop;

	}

	/**
	 * Sets the padding of the body element to match the height of the provided header element.
	 * This method updates the CSS variable '--header-height' on the body element.
	 *
	 * @return {undefined}
	 */
	#setBodyPadding (){

		document.body.style.setProperty( '--header-height', `${this.#headerEl.scrollHeight}px` );

	}

	/**
	 * Lock page scroll
	 *
	 * @return {void}
	 */
	#lockScroll (){

		this.#scrollPosition = window.scrollY;
		document.body.style.overflow = 'hidden';
		document.body.style.position = 'fixed';
		document.body.style.top = `-${this.#scrollPosition}px`;
		document.body.style.width = '100%';
		document.documentElement.style.scrollBehavior = 'auto'

	}

	/**
	 * Unlock page scroll
	 *
	 * @return {void}
	 */
	#unlockScroll (){

		document.body.style.removeProperty( 'overflow' );
		document.body.style.removeProperty( 'position' );
		document.body.style.removeProperty( 'top' );
		document.body.style.removeProperty( 'width' );
		window.scrollTo( 0, this.#scrollPosition );
		setTimeout( () => {

			document.documentElement.style.scrollBehavior = 'smooth'

		}, 500 );

	}

	/**
	 * Handles the click event on the body element.
	 *
	 * @param {Event} e - The event object representing the click event.
	 *
	 * @return {void}
	 */
	#handleBodyClick ( e ){

		if ( !e.target ) {

			return;

		}

		if ( !this.#headerEl.contains( e.target ) ) {

			this.#closeAllNavSubmenus();
			this.#closeLangSwitcher();
			this.#closeAllNavTopItems();

			if ( this.#headerEl.classList.contains( 'page-header--menu-active' ) ) {

				this.#handleMenuTrigger();

			}

		} else if ( !e.target.closest( '.c-menu-header__submenu' ) && !e.target.closest( '.js-menu-header-trigger' ) ) {

			this.#closeAllNavSubmenus();

		}

	}

	/**
	 * Handles key down event to check if the Escape key was pressed and if a mega menu is active to close it.
	 *
	 * @param {KeyboardEvent} e - The KeyboardEvent object representing the key down event.
	 *
	 * @return {void}
	 */
	#handleKeyDown ( e ){

		if ( e.key === 'Escape' || e.key === 'Esc' ) {

			if ( this.#headerEl.classList.contains( 'page-header--menu-active' ) ) {

				this.#handleMenuTrigger();

			}

		}

	}

	/**
	 * Close single nav submenu
	 *
	 * @param {HTMLElement} item
	 * @return {void}
	 */
	#closeNavSubmenu ( item ){

		const trigger = item.querySelector( '.js-menu-header-trigger' );
		item.classList.remove( 'c-menu-header__item--open' );
		if ( trigger ) {

			trigger.setAttribute( 'aria-expanded', 'false' );

		}

	}

	/**
	 * Close all open nav submenus
	 *
	 * @return {void}
	 */
	#closeAllNavSubmenus (){

		this.#headerEl.querySelectorAll( '.js-menu-header-has-children.c-menu-header__item--open' ).forEach( ( item ) => {

			this.#closeNavSubmenu( item );

		} );

	}

	/**
	 * Handle nav trigger click — toggles submenu visibility on the parent <li>
	 *
	 * @param {HTMLElement} trigger
	 * @return {void}
	 */
	#handleNavTrigger ( trigger ){

		const item = trigger.closest( '.js-menu-header-has-children' );

		if ( !item ) {

			return;

		}

		const isCurrentlyOpen = item.classList.contains( 'c-menu-header__item--open' );

		this.#closeAllNavSubmenus();

		if ( !isCurrentlyOpen ) {

			item.classList.add( 'c-menu-header__item--open' );
			trigger.setAttribute( 'aria-expanded', 'true' );

		}

	}

	/**
	 * Close language switcher if open
	 *
	 * @return {void}
	 */
	#closeLangSwitcher (){

		if ( !this.#langSwitcherTrigger ) {

			return;

		}

		const switcher = this.#langSwitcherTrigger.closest( '.c-lang-switcher' );

		if ( switcher ) {

			switcher.classList.remove( 'c-lang-switcher--open' );
			this.#langSwitcherTrigger.setAttribute( 'aria-expanded', 'false' );

		}

	}

	/**
	 * Handle language switcher trigger click on mobile — toggles open state
	 *
	 * @return {void}
	 */
	#handleLangSwitcherTrigger (){

		const switcher = this.#langSwitcherTrigger.closest( '.c-lang-switcher' );

		if ( !switcher ) {

			return;

		}

		const isOpen = switcher.classList.contains( 'c-lang-switcher--open' );

		switcher.classList.toggle( 'c-lang-switcher--open', !isOpen );
		this.#langSwitcherTrigger.setAttribute( 'aria-expanded', String( !isOpen ) );

	}

	/**
	 * Close all open nav-top items
	 *
	 * @return {void}
	 */
	#closeAllNavTopItems (){

		this.#headerEl.querySelectorAll( '.c-nav-top__item--open' ).forEach( ( item ) => {

			const trigger = item.querySelector( '.js-nav-top-trigger' );
			item.classList.remove( 'c-nav-top__item--open' );

			if ( trigger ) {

				trigger.setAttribute( 'aria-expanded', 'false' );

			}

		} );

	}

	/**
	 * Close a single nav-top item
	 *
	 * @param {HTMLElement} item
	 * @return {void}
	 */
	#closeNavTopItem ( item ){

		const trigger = item.querySelector( '.js-nav-top-trigger' );
		item.classList.remove( 'c-nav-top__item--open' );

		if ( trigger ) {

			trigger.setAttribute( 'aria-expanded', 'false' );

		}

	}

	/**
	 * Handle nav-top trigger click — toggles open state per item
	 *
	 * @param {HTMLElement} trigger
	 * @return {void}
	 */
	#handleNavTopTrigger ( trigger ){

		const item = trigger.closest( '.c-nav-top__item' );

		if ( !item ) {

			return;

		}

		const isOpen = item.classList.contains( 'c-nav-top__item--open' );

		this.#closeAllNavTopItems();

		if ( !isOpen ) {

			item.classList.add( 'c-nav-top__item--open' );
			trigger.setAttribute( 'aria-expanded', 'true' );

		}

	}

	/**
	 * Handle menu trigger
	 *
	 * @return {void}
	 */
	#handleMenuTrigger (){

		this.#headerEl.classList.toggle( 'page-header--menu-active' );
		this.#menuTrigger.classList.toggle( 'page-header__burger--active' );

		const isActive = this.#headerEl.classList.contains( 'page-header--menu-active' );

		this.#menuTrigger.setAttribute( 'aria-expanded', isActive ? 'true' : 'false' );

		if ( isActive ) {

			this.#lockScroll();
			this.#setDropdownHeights();

		} else {

			this.#unlockScroll();

		}

	}

	/**
	 * Set CSS height variable for all header dropdown components
	 *
	 * @return {void}
	 */
	#setDropdownHeights (){

		// Lang switcher
		const langSwitcher = this.#headerEl.querySelector( '.c-lang-switcher' );

		if ( langSwitcher ) {

			const wrapper = langSwitcher.querySelector( '.c-lang-switcher__wrapper' );

			if ( wrapper ) {

				langSwitcher.style.setProperty( '--lang-list-total-height', `${wrapper.scrollHeight}px` );

			}

		}

		// Nav top — per item (each item has its own dropdown height)
		this.#headerEl.querySelectorAll( '.c-nav-top__item--has-children' ).forEach( ( item ) => {

			const wrapper = item.querySelector( '.c-nav-top__wrapper' );

			if ( wrapper ) {

				item.style.setProperty( '--nav-top-dropdown-height', `${wrapper.scrollHeight}px` );

			}

		} );

	}

	/**
	 * Public method for cleanup
	 *
	 * @return {void}
	 */
	destroy (){

		this.#menuTrigger.removeEventListener( 'click', this.#handleMenuTrigger );
		document.removeEventListener( 'keydown', this.#handleKeyDown.bind( this ) );
		document.removeEventListener( 'click', this.#handleBodyClick.bind( this ) );
		this.#unlockScroll();
		this.#headerEl.classList.remove( 'page-header--menu-active' );

	}

}


/**
 * Initialize menu
 *
 * @return {void}
 */
export const initMenu = () => {

	new Menu();

};
